import { useRef } from "react";
import { Reveal, SectionHeader } from "./shared";
import { techStack } from "../../data/portfolioData";

const BRAND = {
  JavaScript: "#F7DF1E", Java: "#F89820", Dart: "#0175C2", C: "#A8B9CC", Kotlin: "#7F52FF",
  React: "#61DAFB", "React Native": "#61DAFB", Flutter: "#54C5F8",
  "Node.js": "#8CC84B", "Express.js": "#E2E8F0", "Spring Boot": "#6DB33F", MQTT: "#C084FC",
  PostgreSQL: "#58A6D6", MongoDB: "#47A248", Supabase: "#3ECF8E", Firebase: "#FFCA28",
  AWS: "#FF9900", "Google Cloud": "#4285F4", Vercel: "#E2E8F0", Render: "#46E3B7",
  Git: "#F05032", GitHub: "#E2E8F0", "VS Code": "#3EAAF5", "Android Studio": "#3DDC84", Xcode: "#58A6F5", Expo: "#B8B3FF",
};

const Chip = ({ name, index }) => {
  const color = BRAND[name] || "#F59E0B";
  return (
    <span
      className="group/chip inline-flex shrink-0 cursor-default items-center gap-2.5 rounded-full border border-white/[0.08] bg-[#0F1420] px-4 py-2 text-sm font-medium text-slate-300 transition-all duration-300 hover:-translate-y-0.5 hover:text-slate-100"
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${color}66`;
        e.currentTarget.style.boxShadow = `0 0 26px -8px ${color}90`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "";
        e.currentTarget.style.boxShadow = "";
      }}
      data-testid={`tech-item-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${index > 0 ? "-dup" : ""}`}
    >
      <span
        className="h-2 w-2 rounded-full transition-transform duration-300 group-hover/chip:scale-125"
        style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
      />
      {name}
    </span>
  );
};

export const TechStackSection = () => {
  const ref = useRef(null);
  const total = techStack.reduce((n, g) => n + g.items.length, 0);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="stack" className="py-20 lg:py-28 bg-[#090C13]" data-testid="tech-stack-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="06"
          eyebrow="Tech Arsenal"
          title="Technologies I Work With"
          description="A deliberate, production-proven stack — chosen for reliability, speed of shipping and scale."
          testId="stack-header"
        />

        <Reveal>
          <p className="mb-8 font-code text-xs uppercase tracking-[0.25em] text-slate-500" data-testid="stack-counter">
            <span className="text-amber-400">{total} technologies</span> · {techStack.length} disciplines · hover a row to explore
          </p>
        </Reveal>

        <div
          ref={ref}
          onMouseMove={onMove}
          className="stack-spotlight space-y-3.5 overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0B0F19]/60 p-4 sm:p-6"
          data-testid="stack-marquee-board"
        >
          {techStack.map((g, gi) => (
            <Reveal key={g.group} delay={gi * 0.07}>
              <div
                className="group/row flex flex-col gap-3 rounded-2xl border border-white/[0.05] bg-white/[0.015] px-4 py-4 transition-colors duration-300 hover:border-white/[0.12] sm:flex-row sm:items-center sm:gap-6 sm:px-5"
                data-testid={`tech-group-${gi}`}
              >
                <p className="shrink-0 font-code text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors duration-300 group-hover/row:text-amber-400/90 sm:w-44">
                  <span className="mr-2 text-slate-700">{String(gi + 1).padStart(2, "0")}</span>
                  {g.group}
                </p>
                <div className="relative flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
                  <div
                    className={`marquee-track ${gi % 2 ? "marquee-reverse" : ""} flex w-max gap-3 py-0.5`}
                    style={{ animationDuration: `${36 + gi * 7}s` }}
                  >
                    {[...g.items, ...g.items].map((item, i) => (
                      <Chip key={`${item}-${i}`} name={item} index={i} />
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
